import { useState } from 'react'
import { organizationService } from '../../services/organizationService'
import type { Organization, OrganizationCreate, OrganizationUpdate } from '../../types'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'

interface OrganizationFormProps {
  organization?: Organization
  onSuccess?: (org: Organization) => void
  onCancel?: () => void
}

export function OrganizationForm({ organization, onSuccess, onCancel }: OrganizationFormProps) {
  const [formData, setFormData] = useState<OrganizationCreate>({
    name: organization?.name || '',
    description: organization?.description || '',
    industry: organization?.industry || '',
    size: organization?.size || undefined,
    website: organization?.website || '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      let result: Organization
      if (organization) {
        result = await organizationService.update(organization.id, formData as OrganizationUpdate)
      } else {
        result = await organizationService.create(formData)
      }
      onSuccess?.(result)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save organization')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{organization ? 'Edit Organization' : 'New Organization'}</CardTitle>
        <CardDescription>
          {organization ? 'Update organization details' : 'Create a new organization'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="name">Organization Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Acme Corporation"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="industry">Industry</Label>
            <Input
              id="industry"
              value={formData.industry}
              onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
              placeholder="Technology, Healthcare, Finance, etc."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="size">Organization Size</Label>
            <Select
              value={formData.size}
              onValueChange={(value) =>
                setFormData({ ...formData, size: value as any })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Small (1-50 employees)</SelectItem>
                <SelectItem value="medium">Medium (51-500 employees)</SelectItem>
                <SelectItem value="large">Large (501-5000 employees)</SelectItem>
                <SelectItem value="enterprise">Enterprise (5000+ employees)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="https://example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the organization..."
              rows={4}
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Saving...' : organization ? 'Update' : 'Create'}
            </Button>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
